import React, {useCallback, useEffect, useMemo, useRef, useState} from 'react';
import {Icon, IconButton, Text, VStack} from '@chakra-ui/react';
import {useNavigate, useSearchParams} from "react-router-dom";
import {Plus} from 'lucide-react';

import {useApi} from "@/uses/useApi";
import useScreenState from "@/uses/useScreenState";
import {
    EntityListBody,
    EntityListBodySkeleton,
    EntityListHeader,
    EntityListHeaderSkeleton,
    EntityModalContent,
    ListHeader,
    Paginator,
    PaginatorSkeleton
} from "@/components";
import {useModal} from "@/contexts/modalContext";
import {useLanguage} from "@/contexts/languageContext";
import objectUtil from "@/utils/objectUtil";

const PER_PAGE = 20;

/**
 * Page de gestion d'une liste de référence (clubs, divisions).
 * `getFilters` reçoit la distribution des facettes et renvoie les filtres
 * proposés dans l'en-tête.
 */
export const ReferenceList = ({entity, Body, BodySkeleton, EditBody, getFilters = null, description = null, sorts = null, getDeletionText = null}) => {
    const navigate = useNavigate();
    const {language, t} = useLanguage();
    const sizes = {base: '95%', md: '95%', lg: '90%'};
    const api = useApi();
    const [entities, setEntities] = useState([]);
    const [filters, setFilters] = useState({});

    const {open, close} = useModal();
    const [itemNumber, setItemNumber] = useState(null);

    const possibleSorts = sorts ?? [
        ['label', 'asc', t('sentences.sortByField', {field: t('fields.label'), order: t('orders.asc')})],
        ['label', 'desc', t('sentences.sortByField', {field: t('fields.label'), order: t('orders.desc')})],
    ];

    const [currentQueryParameters, setSearchParams] = useSearchParams();
    const getDefaultState = useCallback((pageParams) => {
        const result = {};

        /** Retrieve filters param */
        try {
            result.filter = JSON.parse(pageParams.get('filter'));
            if (objectUtil.isObject(result.filter)) {
                result.filter = Object.fromEntries(Object.entries(result.filter).filter(([_, value]) => (value ?? []).length > 0));
            }
        } catch (e) {
            result.filter = [];
        }

        /** Retrieve search param */
        result.search = pageParams.get('search') ?? '';

        /** Retrieve sort param */
        const sort = (pageParams.get('sort') ?? '').split('-');
        result.sort = possibleSorts.find(([col, type]) => sort[0] === col && sort[1] === type) ?? possibleSorts[0];

        /** Retrieve page param */
        const parsedPage = parseInt(pageParams.get('page'), 10);
        result.page = isNaN(parsedPage) || parsedPage <= 0 ? 0 : parsedPage - 1;
        return result;
    }, []);
    const defaultParams = getDefaultState(currentQueryParameters);
    const [getParams, setGetParams] = useState({
        sort: defaultParams.sort,
        ...(defaultParams.page > 0 ? {page: defaultParams.page} : {}),
        ...(defaultParams.search && defaultParams.search.length >= process.env.REACT_APP_MIN_SEARCH_LENGTH ? {search: defaultParams.search} : {}),
        ...(Object.keys(defaultParams.filter ?? {}).length > 0 ? {filter: defaultParams.filter} : {}),
    });

    const {state, realState, isMounted, setState} = useScreenState();

    const reqParams = useRef(null);
    const getPage = useCallback(() => {
        /** If page is different, it's only a pagination action,
         * so no need to show skeleton for paginator
         */
        const newState = reqParams.current !== null &&
        objectUtil
            .difference(getParams, reqParams.current ?? {})
            .filter((e) => !['page', 'sort'].includes(e))
            .length !== 0
            ? 1 : 0;
        setState((prevState) => prevState === -1 ? prevState : newState);
        reqParams.current = getParams;
        return api.ApiService.get(entity, {
            ...reqParams.current,
            limit: PER_PAGE,
            locale: language,
            ...(state === -1 && getFilters ? {needFilters: 'bool:true'} : {})
        })
            .then(res => {
                if (res?.data?.type === 'success') {
                    setItemNumber(() => res?.data?.value?.estimatedTotalHits);
                    if (getFilters && res?.data?.value?.facetDistribution) {
                        setFilters(() => getFilters(res.data.value.facetDistribution));
                    }
                    setEntities(() => (res?.data?.value?.hits ?? []));
                }
            })
            .catch(e => console.log(e?.response?.data))
            .finally(() => setState(_ => 2));
    }, [getParams, state]);

    useEffect(() => {
        getPage().then(() => {
            /** Manage persistent state of search query */
            setSearchParams(prev => {
                if ('search' in getParams) {
                    prev.set('search', getParams.search);
                } else {
                    prev.delete('search');
                }

                if ('filter' in getParams) {
                    prev.set('filter', JSON.stringify(getParams.filter));
                } else {
                    prev.delete('filter');
                }

                if ('sort' in getParams) {
                    prev.set('sort', `${getParams.sort[0]}-${getParams.sort[1]}`);
                } else {
                    prev.delete('sort');
                }

                if ('page' in getParams) {
                    prev.set('page', (getParams.page + 1).toString());
                } else {
                    prev.delete('page');
                }

                return prev;
            });
        });
    }, [getParams]);

    const hasData = useMemo(() => {
        return isMounted && (entities.length > 0 || Object.keys(objectUtil.filterByKeys(getParams, ['sort'], false)).length > 0);
    }, [isMounted, entities, getParams]);

    const noDataText = t(`sentences.noEntityYet.${entity}`);

    const openCreation = useCallback(() => {
        open(<EntityModalContent
            title={t('actions.add')}
            Body={<EditBody successCallback={() => navigate(0)}/>}
            close={close}
        />, 'cover');
    }, [open, close, navigate]);

    const Table = useMemo(() => {
        return hasData
            ?
            <VStack position="relative" w="100%" overflow="hidden" flex={1} pb="2vh">
                <EntityListHeader
                    after={<IconButton bg="secondary.500" onClick={openCreation}>
                        <Icon as={Plus}/>
                    </IconButton>}
                    setGetParams={setGetParams}
                    filters={filters}
                    defaultParams={defaultParams}
                    sizes={sizes}
                    possibleSorts={possibleSorts}
                />
                <VStack
                    flex={1}
                    w={sizes}
                    py="1rem"
                    align="center"
                    justify="flex-start"
                    overflow="auto"
                    color="secondary.500"
                >
                    {
                        [0, 1].includes(realState)
                            ? <EntityListBodySkeleton Body={BodySkeleton} sizes={sizes}/>
                            : <EntityListBody
                                noDataText={noDataText}
                                entity={entity}
                                Body={Body}
                                EditBody={EditBody}
                                sizes={sizes}
                                entities={entities}
                                setEntities={setEntities}
                                getDeletionText={getDeletionText ?? ((e) => `"${e?.label ?? ''}"`.slice(0, 200))}
                            />
                    }
                </VStack>
                {
                    realState === 1
                        ? <PaginatorSkeleton/>
                        : <Paginator
                            defaultPage={defaultParams.page}
                            setGetParams={setGetParams}
                            itemNumber={itemNumber}
                            perPage={PER_PAGE}
                        />
                }
            </VStack>
            :
            <VStack align="center" justify="center">
                <Text>{noDataText}</Text>
                <IconButton bg="secondary.500" onClick={openCreation}>
                    <Icon as={Plus}/>
                </IconButton>
            </VStack>;
    }, [hasData, realState, entities, setEntities, setGetParams, filters, defaultParams, sizes, itemNumber, openCreation]);

    const Content = useMemo(() => {
        return isMounted
            ? Table
            : <VStack w="100%" overflow="hidden" flex={1} pb="2vh">
                <EntityListHeaderSkeleton
                    canSort
                    hasFilters={Boolean(getFilters)}
                    sizes={sizes}
                    after={<IconButton/>}
                />
                <VStack
                    flex={1}
                    w={sizes}
                    py="1rem"
                    align="center"
                    justify="flex-start"
                    overflow="auto"
                    color="secondary.500"
                >
                    <EntityListBodySkeleton Body={BodySkeleton} sizes={sizes}/>
                </VStack>
                <PaginatorSkeleton/>
            </VStack>;
    }, [isMounted, Table]);

    const entityLabel = t(`objects.${entity}_other`);

    return (
        <VStack w="100%" overflow="hidden">
            <ListHeader
                title={t('sentences.managementOfEntities', {entity: entityLabel})}
                description={description ?? t('sentences.manageEntities', {
                    action1: t('actions.add'),
                    action2: t('actions.edit'),
                    action3: t('actions.view'),
                    entity: entityLabel
                })}
            />
            {Content}
        </VStack>
    );
};

export default ReferenceList;
